var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import useModal, { ModalState } from '@delangle/use-modal';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { buildUseOnlyOneInstanceOpened } from '../_internal/buildUseOnlyOneInstanceOpened';
import { isFunction } from '../_internal/data';
import { useDisableScroll } from '../_internal/useDisableScroll';
import { useWindowSize } from '../_internal/useWindowSize';
import { ANIMATION_DURATIONS } from '../animations';
import { breakpoints } from '../breakpoints';
import { Modal } from '../Modal';
import { useCurrentBackground } from '../useCurrentBackground';
import { withTriggerElement } from '../withTriggerElement';
import { TogglePanelContext } from './TogglePanel.context';
import { Container, Overlay } from './TogglePanel.style';
var _a = buildUseOnlyOneInstanceOpened(), useOnlyOneInstanceOpened = _a.useOnlyOneInstanceOpened, useInstanceContext = _a.useInstanceContext, InstanceProvider = _a.InstanceProvider;
export var useParentTogglePanel = useInstanceContext;
var InnerTogglePanel = React.forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children, _c = _a.fullScreenOnMobile, fullScreenOnMobile = _c === void 0 ? false : _c, onClose = _a.onClose, onOpen = _a.onOpen, open = _a.open, setStyle = _a.setStyle, style = _a.style, triggerRef = _a.triggerRef, _d = _a.withOverlay, withOverlay = _d === void 0 ? true : _d, props = __rest(_a, ["children", "fullScreenOnMobile", "onClose", "onOpen", "open", "setStyle", "style", "triggerRef", "withOverlay"]);
    var _e = React.useState(style), customStyle = _e[0], setCustomStyle = _e[1];
    var instanceId = useOnlyOneInstanceOpened({ open: open, onClose: onClose });
    var modal = useModal({
        ref: ref,
        open: open,
        onClose: onClose,
        persistent: false,
        animated: true,
        animationDuration: ANIMATION_DURATIONS.l,
    });
    useDisableScroll({ enabled: open, ignoreRef: modal.ref });
    var shouldRenderModal = modal.hasAlreadyBeenOpened;
    var updateStyle = React.useCallback(function () {
        var _a;
        if (!shouldRenderModal) {
            return undefined;
        }
        var modalElement = (_a = modal.ref) === null || _a === void 0 ? void 0 : _a.current;
        var triggerElement = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current;
        if (!setStyle || !triggerElement || !modalElement) {
            return setCustomStyle(style);
        }
        var modalBoundingRect = modalElement.getBoundingClientRect();
        var triggerBoundingRect = triggerElement.getBoundingClientRect();
        setCustomStyle(__assign(__assign({}, setStyle({
            /*
             * The dimensions returned by `getBoundingClientRect` take into account CSS transformations.
             * `offsetWidth` and `offsetHeight` includes scrollbars and borders.
             * Overriding the width and height prevents wrong placement of the panel, especially during opening transitions.
             */
            bottom: modalBoundingRect.bottom,
            height: modalElement.offsetHeight,
            left: modalBoundingRect.left,
            right: modalBoundingRect.right,
            top: modalBoundingRect.top,
            width: modalElement.offsetWidth,
        }, {
            bottom: triggerBoundingRect.bottom,
            height: triggerBoundingRect.height,
            left: triggerBoundingRect.left,
            right: triggerBoundingRect.right,
            top: triggerBoundingRect.top,
            width: triggerBoundingRect.width,
        })), style));
    }, [modal.ref, setStyle, shouldRenderModal, triggerRef]);
    var parent = React.useContext(TogglePanelContext);
    var backgroundColor = useCurrentBackground({ useRootTheme: true });
    var dimension = useWindowSize();
    React.useEffect(function () {
        updateStyle();
    }, [dimension, open, updateStyle]);
    React.useEffect(function () {
        if (open) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    if (!shouldRenderModal) {
        return null;
    }
    var content = (React.createElement(InstanceProvider, { id: instanceId, onClose: onClose }, isFunction(children) ? children(modal) : children));
    if (fullScreenOnMobile && dimension.width < breakpoints.raw.phone) {
        return (React.createElement(TogglePanelContext.Provider, { value: modal },
            React.createElement(Modal, { onClose: onClose, open: open }, content)));
    }
    return ReactDOM.createPortal(React.createElement(TogglePanelContext.Provider, { value: modal },
        withOverlay && modal.state === ModalState.opened && React.createElement(Overlay, null),
        React.createElement(Container, __assign({ backgroundColor: backgroundColor, "data-state": modal.state, ref: modal.ref, style: customStyle }, props), content)), (_b = parent === null || parent === void 0 ? void 0 : parent.ref.current) !== null && _b !== void 0 ? _b : document.body);
});
export var TogglePanel = withTriggerElement({
    forwardRef: true,
})(InnerTogglePanel);
//# sourceMappingURL=TogglePanel.js.map